/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { APIContext } from "../../api/api";
import HelmetLayout from "../../components/HelmetLayout";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import {
  FileBox,
  Search,
  Plus,
  ExternalLink,
  Edit2,
  Layers,
} from "lucide-react";

export default function Templates() {
  const [templateList, setTemplateList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const { listOfAllTemplates } = useContext(APIContext);

  useEffect(() => {
    listOfAllTemplates()
      .then((templates) => {
        setTemplateList(Array.isArray(templates) ? templates : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to load templates");
        setTemplateList([]);
        setLoading(false);
      });
  }, []);

  const filteredTemplates = useMemo(() => {
    return templateList.filter((template) => {
      const name = (template.template_name || "").toLowerCase();
      const slug = (template.template_slug || "").toLowerCase();
      const q = searchQuery.toLowerCase().trim();
      return !q || name.includes(q) || slug.includes(q);
    });
  }, [templateList, searchQuery]);

  return (
    <>
      <HelmetLayout title="Templates" />
      <div className="space-y-6 pb-12">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Templates</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Explore, preview, and manage your website templates library.
            </p>
          </div>
          <Link
            to="/templates/add"
            className="bg-[#623ce7] hover:bg-[#5030bc] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-[#623ce7]/20 flex items-center gap-2"
          >
            <Plus size={16} />
            Add New Template
          </Link>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Total Templates</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loading ? "—" : templateList.length}
              </h3>
            </div>
            <div className="p-3 bg-[#623ce7]/10 text-[#623ce7] rounded-xl">
              <FileBox size={22} />
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex justify-between items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search templates by name or slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
            />
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex flex-col justify-center items-center py-24">
            <PulseLoader color="#623ce7" size={12} />
            <p className="text-slate-400 text-sm mt-3">Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500 shadow-sm">
            <Layers className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="font-semibold text-slate-700 text-base">No templates found</p>
            <p className="text-sm text-slate-400 mt-1">
              {searchQuery ? "Try refining your search query." : "Click 'Add New Template' to create one."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTemplates.map((template) => {
              const templateID = template.pk.replace("template-", "");

              return (
                <div
                  key={templateID}
                  className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#623ce7]/50 transition-all duration-300 hover:shadow-lg hover:shadow-[#623ce7]/5 flex flex-col"
                >
                  {/* Thumbnail & Hover Overlay */}
                  <div className="relative h-48 bg-slate-100 overflow-hidden">
                    <img
                      src={template.template_thumbnail || "https://picsum.photos/seed/placeholder/400/250"}
                      alt={template.template_name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = "https://picsum.photos/seed/placeholder/400/250";
                      }}
                    />
                    <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-3">
                      <a
                        href={`https://${template.template_slug}.grigora.app`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2.5 bg-white rounded-full text-slate-900 hover:bg-slate-100 transition-colors shadow-md"
                        title="Preview Live Template"
                      >
                        <ExternalLink size={18} />
                      </a>
                      <Link
                        to={`/templates/${templateID}`}
                        className="p-2.5 bg-[#623ce7] rounded-full text-white hover:bg-[#5030bc] transition-colors shadow-md"
                        title="Edit Template"
                      >
                        <Edit2 size={18} />
                      </Link>
                    </div>

                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-md text-xs font-mono text-slate-700 font-medium shadow-sm border border-slate-100">
                      {template.template_slug}
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <Link
                        to={`/templates/${templateID}`}
                        className="font-semibold text-slate-900 hover:text-[#623ce7] transition-colors text-base truncate block mb-1"
                        title={template.template_name}
                      >
                        {template.template_name}
                      </Link>
                      <p className="text-xs text-slate-400 font-mono mb-3">
                        {template.template_slug}.grigora.app
                      </p>
                    </div>

                    {/* Bottom Actions */}
                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-2 mt-auto">
                      <a
                        href={`https://${template.template_slug}.grigora.app`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 transition-colors"
                      >
                        Live Preview <ExternalLink size={12} />
                      </a>
                      <Link
                        to={`/templates/${templateID}`}
                        className="text-xs font-medium bg-[#623ce7]/10 hover:bg-[#623ce7]/20 text-[#623ce7] px-2.5 py-1 rounded transition-colors"
                      >
                        Edit
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
